import { Modal, Badge } from '../../../shared/components';
import { AuditLogEntry, OperationTimesSnapshot } from '../../../shared/services/audit/types';
import { formatDateTime, formatDuration } from '../../../shared/utils/datetime';

function isOperationTimesSnapshot(data: unknown): data is OperationTimesSnapshot {
  return !!data && typeof data === 'object' && 'code' in data && 'activityName' in data && 'startDateTime' in data;
}

/** Una fila de campo, resaltada en amarillo si cambio entre Antes y Despues. */
function FieldRow({ label, before, after, changed }: { label: string; before: string; after?: string; changed?: boolean }) {
  return (
    <div className="grid grid-cols-3 gap-2 border-b border-ink-50 py-2 text-sm last:border-0">
      <span className="text-ink-400">{label}</span>
      <span className={changed ? 'rounded bg-amber-100 px-1.5 font-medium text-amber-800' : 'text-ink-700'}>{before}</span>
      {after !== undefined && <span className={changed ? 'rounded bg-brand-light px-1.5 font-medium text-brand-700' : 'text-ink-700'}>{after}</span>}
    </div>
  );
}

export function AuditDetailModal({ entry, onClose }: { entry: AuditLogEntry | null; onClose: () => void }) {
  if (!entry) return null;

  const before = isOperationTimesSnapshot(entry.previousData) ? entry.previousData : null;
  const after = isOperationTimesSnapshot(entry.newData) ? entry.newData : null;
  const isDeleted = entry.action === 'DELETE';

  return (
    <Modal open={!!entry} onClose={onClose} title={`Detalle de auditoría · ${entry.entityCode ?? entry.entityId}`}>
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge tone={isDeleted ? 'critical' : 'warning'}>{isDeleted ? 'Registro eliminado' : 'Registro modificado'}</Badge>
          <span className="text-xs text-ink-400">
            {formatDateTime(entry.performedAt)} · por {entry.performedBy}
          </span>
        </div>

        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">Motivo</p>
          <p className="rounded-xl bg-ink-50 p-3 text-sm text-ink-700">{entry.reason}</p>
        </div>

        {before ? (
          <div>
            <div className="grid grid-cols-3 gap-2 pb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">
              <span>Campo</span>
              <span>{isDeleted ? 'Registro (eliminado)' : 'Antes'}</span>
              {!isDeleted && <span>Después</span>}
            </div>
            <FieldRow label="Actividad" before={before.activityName} after={after?.activityName} changed={!isDeleted && before.activityName !== after?.activityName} />
            <FieldRow label="Usuario" before={before.userName} after={after ? after.userName : undefined} changed={!isDeleted && before.userName !== after?.userName} />
            <FieldRow label="Inicio" before={formatDateTime(before.startDateTime)} after={after ? formatDateTime(after.startDateTime) : undefined} changed={!isDeleted && before.startDateTime !== after?.startDateTime} />
            <FieldRow label="Fin" before={formatDateTime(before.endDateTime)} after={after ? formatDateTime(after.endDateTime) : undefined} changed={!isDeleted && before.endDateTime !== after?.endDateTime} />
            <FieldRow
              label="Duración"
              before={formatDuration(before.durationSeconds)}
              after={after ? formatDuration(after.durationSeconds) : undefined}
              changed={!isDeleted && before.durationSeconds !== after?.durationSeconds}
            />
            <FieldRow
              label="Observaciones"
              before={before.observations || '—'}
              after={after ? after.observations || '—' : undefined}
              changed={!isDeleted && (before.observations ?? '') !== (after?.observations ?? '')}
            />
          </div>
        ) : (
          <pre className="max-h-64 overflow-auto rounded-xl bg-ink-50 p-3 text-xs text-ink-600">{JSON.stringify(entry.previousData, null, 2)}</pre>
        )}
      </div>
    </Modal>
  );
}
